import { useTranslation } from 'react-i18next';
import { Button, Modal, Switch } from '@teo/components';
import { useState, useEffect, useRef } from 'react';
import { Portal } from '@/components/Portal';

const PREVIEW_CONSTRAINTS = {
  audio: false, // <— important: never open mic during preview
  video: {
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    facingMode: { ideal: 'environment' },
  },
};

let VIDEO_OPTIONS = { mimeType: 'video/webm;codecs=vp9' };
let mimeType = 'webm';
let videoType = 'video/webm';
if (typeof MediaRecorder !== 'undefined') {
  if (!MediaRecorder.isTypeSupported(VIDEO_OPTIONS.mimeType)) {
    VIDEO_OPTIONS = { mimeType: 'video/mp4; codecs="avc1.42E01E, mp4a.40.2"' };
    mimeType = 'mp4';
    videoType = 'video/mp4';
  }
}

export const VideoCameraModal = ({
  setIsOpenVideoModal,
  isOpenVideoModal,
  setUploadFile,
  forceSound = false,
}) => {
  const { t } = useTranslation();

  const videoRef = useRef(null);
  const recordedVideoRef = useRef(null);
  const micStreamRef = useRef(null);

  // Streams/recorder kept in refs to avoid re-renders during teardown
  const previewStreamRef = useRef(null); // camera only (no audio)
  const recStreamRef = useRef(null); // temp combined stream used only while recording
  const mediaRecorderRef = useRef(null);

  const [recording, setRecording] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);

  const [turnOnSound, setTurnOnSound] = useState(forceSound);

  const [videoInputs, setVideoInputs] = useState([]);
  const [cameraIndex, setCameraIndex] = useState(0);
  const [canSwitch, setCanSwitch] = useState(false);

  const shuttingDownRef = useRef(false);

  // ---------- helpers ----------
  const clearVideoEl = (el) => {
    if (!el) return;
    try {
      el.pause && el.pause();
    } catch {
      /*/ ignore */
    }
    try {
      el.srcObject = null;
    } catch {
      /*/ ignore */
    }
    try {
      el.removeAttribute('srcObject');
    } catch {
      /*/ ignore */
    }
    try {
      el.removeAttribute('src');
    } catch {
      /*/ ignore */
    }
    try {
      el.load && el.load();
    } catch {
      /*/ ignore */
    }
  };

  const revokeRecordedURL = () => {
    const r = recordedVideoRef.current;
    if (r && r.src && r.src.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(r.src);
      } catch {
        /*/ ignore */
      }
      try {
        r.removeAttribute('src');
        r.load && r.load();
      } catch {
        /*/ ignore */
      }
    }
  };

  const stopTracksAndWait = async (stream) => {
    if (!stream) return;
    const tracks = stream.getTracks();
    // Disable first (helps some drivers)
    tracks.forEach((t) => {
      try {
        t.enabled = false;
      } catch {
        /*/ ignore */
      }
    });
    await Promise.all(
      tracks.map(
        (t) =>
          new Promise((resolve) => {
            const finish = () => {
              try {
                t.onended = null;
                t.oninactive = null;
              } catch {
                /*/ ignore */
              }
              resolve();
            };
            let timer = setTimeout(finish, 500);
            try {
              t.onended = finish;
              t.oninactive = finish;
            } catch {
              /*/ ignore */
            }
            try {
              t.stop();
            } catch {
              finish();
            }
            if (t.readyState === 'ended') {
              clearTimeout(timer);
              finish();
            }
          })
      )
    );
  };

  const killPreview = async () => {
    await stopTracksAndWait(previewStreamRef.current);
    previewStreamRef.current = null;
    clearVideoEl(videoRef.current);
  };

  const killRecorder = async () => {
    const mr = mediaRecorderRef.current;

    if (mr && mr.state !== 'inactive') {
      // Створюємо Promise, щоб дочекатися завершення onstop
      await new Promise((resolve) => {
        mr.onstop = async () => {
          // 🔹 Після формування Blob тут можна чистити треки
          await stopTracksAndWait(recStreamRef.current);
          recStreamRef.current = null;

          await stopTracksAndWait(micStreamRef.current);
          micStreamRef.current = null;

          resolve();
        };

        try {
          mr.stop(); // ❗ без mr.requestData()
        } catch {
          resolve();
        }
      });
    }
    mediaRecorderRef.current = null;
  };

  const hardStopAll = async () => {
    await killRecorder(); // stop recording stream if any
    await killPreview(); // stop preview stream

    await fullyStopTracks(recStreamRef.current);
    await fullyStopTracks(micStreamRef.current);

    recStreamRef.current = null;
    micStreamRef.current = null;
    revokeRecordedURL();
    await new Promise((r) => requestAnimationFrame(r));
  };

  const enumerateVideoInputs = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const vids = devices.filter((d) => d.kind === 'videoinput');
      setVideoInputs(vids);
      setCanSwitch(vids.length > 1);
      setCameraIndex((idx) => Math.min(idx, Math.max(0, vids.length - 1)));
    } catch (e) {
      setVideoInputs([]);
      setCanSwitch(false);
    }
  };

  const buildPreviewConstraintsForIndex = (idx) => {
    const base = JSON.parse(JSON.stringify(PREVIEW_CONSTRAINTS)); // audio:false
    const dev = videoInputs[idx];
    if (dev?.deviceId) {
      base.video.deviceId = { exact: dev.deviceId };
      delete base.video.facingMode;
    }
    return base;
  };

  const startPreview = async (idx = 0) => {
    if (shuttingDownRef.current) return;
    await killPreview(); // ensure no live preview
    const constraints =
      videoInputs.length > 0
        ? buildPreviewConstraintsForIndex(idx)
        : PREVIEW_CONSTRAINTS;

    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    if (shuttingDownRef.current) {
      stream.getTracks().forEach((t) => {
        try {
          t.stop();
        } catch {
          /*/ ignore */
        }
      });
      return;
    }

    previewStreamRef.current = stream;
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      await videoRef.current.play().catch(() => {});
    }
    await enumerateVideoInputs();
  };
  // -----------------------------

  // Mount/unmount + open/close
  useEffect(() => {
    if (isOpenVideoModal && !previewStreamRef.current) {
      startPreview(cameraIndex).catch(() => {});
    }
    if (!isOpenVideoModal) {
      shuttingDownRef.current = true;
      (async () => {
        await hardStopAll();
        setHasRecording(false);
        setRecordedBlob(null);
        shuttingDownRef.current = false;
      })();
    }
    return () => {
      shuttingDownRef.current = true;
      (async () => {
        await hardStopAll();
        shuttingDownRef.current = false;
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenVideoModal]);

  // If the user toggles sound while *not* recording, no need to change preview (still audio:false)
  const startRecording = async () => {
    if (recording || mediaRecorderRef.current) return; // guard
    setHasRecording(false);
    setRecordedBlob(null);

    // clone the preview video track so stopping the recorder doesn't kill preview
    const previewVideoTrack = previewStreamRef.current?.getVideoTracks()[0];
    if (!previewVideoTrack || previewVideoTrack.readyState !== 'live') {
      // preview died? restart it, then try again
      await startPreview(cameraIndex);
    }
    const videoTrackClone = previewStreamRef.current
      ?.getVideoTracks()[0]
      ?.clone();
    if (!videoTrackClone) return;

    // optional audio
    micStreamRef.current = null;
    if (turnOnSound) {
      try {
        micStreamRef.current = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
      } catch (e) {
        // if mic fails, continue with video-only recording
        micStreamRef.current = null;
      }
    }

    const tracks = [videoTrackClone];
    if (micStreamRef.current) {
      const at = micStreamRef.current.getAudioTracks()[0];
      if (at) tracks.push(at);
    }

    const recStream = new MediaStream(tracks);
    recStreamRef.current = recStream;

    try {
      const mr = new MediaRecorder(recStream, VIDEO_OPTIONS);
      mediaRecorderRef.current = mr;

      const chunks = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunks, { type: videoType });
        setRecordedBlob(blob);
        const url = URL.createObjectURL(blob);
        if (recordedVideoRef.current) {
          recordedVideoRef.current.src = url;
          recordedVideoRef.current.controls = true;
          recordedVideoRef.current.load(); // 🔹 критично
        }

        setHasRecording(true);
      };

      mr.start();
      setRecording(true);
    } catch (err) {
      console.error('MediaRecorder start failed', err);
      // clean up if start failed
      tracks.forEach((t) => {
        try {
          t.stop();
        } catch {
          /*/ ignore */
        }
      });
      recStreamRef.current = null;
      mediaRecorderRef.current = null;
    }
  };

  const fullyStopTracks = async (stream) => {
    if (!stream) return;
    stream.getTracks().forEach((track) => {
      try {
        track.stop();
        track.enabled = false;
      } catch {
        /* ignore */
      }
    });
  };

  const stopRecording = async () => {
    setRecording(false);

    try {
      mediaRecorderRef.current.stop();
    } catch {
      console.error();
    }

    await killRecorder();

    await fullyStopTracks(recStreamRef.current);
    recStreamRef.current = null;

    await fullyStopTracks(micStreamRef.current);
    micStreamRef.current = null;

    const vt = previewStreamRef.current?.getVideoTracks()[0];
    if (!vt || vt.readyState !== 'live') {
      await startPreview(cameraIndex);
    }
  };

  const insertVideo = async () => {
    if (!recordedBlob) return;
    setUploadFile((prev) => [
      ...prev,
      {
        blob: recordedBlob,
        video: true,
        size: recordedBlob.size,
        type: VIDEO_OPTIONS.mimeType,
        mimeType: mimeType,
      },
    ]);
    shuttingDownRef.current = true;
    await hardStopAll(); // ensure no camera/mic left
    shuttingDownRef.current = false;
    setIsOpenVideoModal(!isOpenVideoModal);
  };

  const closeModal = () => {
    shuttingDownRef.current = true;
    (async () => {
      await hardStopAll(); // kills recorder (mic), combined stream, and preview (camera)
      shuttingDownRef.current = false;
      setIsOpenVideoModal(!isOpenVideoModal);
    })();
  };

  const switchCamera = async () => {
    if (recording) return;
    if (!canSwitch || videoInputs.length === 0) return;
    const nextIndex = (cameraIndex + 1) % videoInputs.length;
    setCameraIndex(nextIndex);
    await startPreview(nextIndex);
  };

  return (
    <Portal root="body">
      <div className="z-10 bg-white">
        <Modal isOpen={isOpenVideoModal}>
          <Modal.Body>
            <div className="max-h-[70vh] overflow-hidden">
              <video
                autoPlay
                muted
                style={{
                  minWidth: '100%',
                  minHeight: '100%',
                  maxWidth: '100%',
                  maxHeight: '65vh',
                  objectFit: 'contain',
                }}
                className={`${hasRecording && 'hidden'}`}
                ref={videoRef}
              ></video>

              <video
                style={{
                  minWidth: '100%',
                  minHeight: '100%',
                  maxWidth: '100%',
                  maxHeight: '65vh',
                  objectFit: 'contain',
                }}
                ref={recordedVideoRef}
                controls
                className={`${!hasRecording && 'hidden'}`}
              ></video>
            </div>
          </Modal.Body>

          {!recording && !forceSound && (
            <div className="px-6">
              <div className="flex flex-row items-center gap-4">
                <div data-testid="reportEmail">
                  <Switch
                    checked={turnOnSound}
                    onChange={(isCheck) => setTurnOnSound(isCheck)}
                  />
                </div>
                <div>
                  <h4 className="font-normal">
                    {t('widgets.upload_buttons.turn_on')}
                  </h4>
                </div>
              </div>
            </div>
          )}

          <Modal.Footer
            cancelButtonLabel={t('widgets.upload_buttons.cancel_button')}
            onCancel={closeModal}
          >
            <div className="flex gap-3">
              {!recording && canSwitch && (
                <Button type="button" onClick={switchCamera}>
                  {t('widgets.upload_buttons.switch_camera') || 'Switch camera'}
                </Button>
              )}

              {!recording ? (
                <Button type="button" onClick={startRecording}>
                  {t('widgets.upload_buttons.record')}
                </Button>
              ) : (
                <Button type="button" onClick={stopRecording}>
                  {t('widgets.upload_buttons.stop')}
                </Button>
              )}

              {!recording && hasRecording && (
                <Button type="button" onClick={insertVideo}>
                  {t('widgets.upload_buttons.insert')}
                </Button>
              )}
            </div>
          </Modal.Footer>
        </Modal>
      </div>
    </Portal>
  );
};
